/**
 * Masking for anything captured from the rendered page before it is sent to an
 * external service.
 *
 * This lives apart from `lumioPageContext` on purpose: that module reaches the
 * Redux store at import time, which pulls in `window.config` and makes it
 * unloadable under jest's node environment. The masking rules are the part that
 * must stay independently verifiable, so they are a dependency-free module.
 */

/**
 * Local part, `@`, domain. The trailing `\w` stops the match at the end of the
 * domain instead of swallowing following punctuation (a sentence period, a
 * comma in a list), which would leave the mask looking like it ate real text.
 */
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]*\w/g;

/**
 * An international number: a leading `+`, then digits and the usual separators.
 *
 * The `+` anchor is what keeps this off the outline's many plain numbers, and
 * the digit floor below keeps it off short signed deltas. Settings > Account
 * renders a linked Telegram number in the same row that renders a linked Google
 * address, so without this the mask would protect one and not the other.
 */
const PHONE_RE = /\+\d[\d\s().-]{5,}\d/g;

/**
 * Shortest international number worth masking. Deltas the studio shows (`+50%`,
 * `+1 more`) fall far below it, and a signed figure large enough to reach eight
 * digits is worth losing to keep a phone number in the browser: the snapshot is
 * display context, never a source of facts.
 */
const PHONE_MIN_DIGITS = 8;

const digitCount = (value: string): number => (value.match(/\d/g) ?? []).length;

/**
 * Mask personal data in text captured from the page.
 *
 * Owner-hub pages render the operator's players, not just the owner using the
 * studio: /account/players puts a player address in the cell text and in the
 * row's aria-label, and both are picked up by the page-context serializer.
 * Those addresses belong to data subjects who never agreed to an AI vendor
 * seeing them, so they are masked before the snapshot leaves the browser. The
 * owner's own contact details are masked on the same principle.
 */
export const redact = (text: string): string =>
  text
    .replace(EMAIL_RE, '[email]')
    .replace(PHONE_RE, (match) => (digitCount(match) >= PHONE_MIN_DIGITS ? '[phone]' : match));

/**
 * Routes whose screen is never captured at all.
 *
 * Masking works on identifiers. It cannot work on a whole record: the players
 * roster renders, per row, a joined date, an acquisition source and four money
 * figures, and those describe an identifiable person whatever the mask does to
 * the address above them.
 *
 * Suppressing the capture here costs nothing that still works. The snapshot
 * exists so the assistant can resolve "this score" or "the second one", and on
 * that page every row already masks down to the same `[email]`, so it cannot
 * tell them apart anyway. It produces no nudges either: those come from
 * `/assistant/context-event`, which never reads the screen.
 *
 * The envelope still reports WHERE the owner is, so the greeting can name the
 * page; only the contents of the screen are withheld.
 */
const NO_SNAPSHOT_ROUTES = ['/account/players'];

/** Is this route one whose screen contents must never leave the browser? */
export const snapshotSuppressed = (pathname: string): boolean =>
  NO_SNAPSHOT_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
